import { useState, useEffect, useRef } from "react";
import { maskDobInput, dobTextToIso, isoToDobText } from "./dobInput";
import { readFileToMemory } from "./fileMemory";

const API = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/onboarding`
  : "http://localhost:8001/onboarding";

// UX-11: textos visibles en ES / PT / EN. Invariante: las tres tablas tienen
// exactamente las mismas claves (se verifica en desarrollo mas abajo).
// Los marcadores *...* envuelven el texto en <em>; [var] se reemplaza en tr().
const TEXT = {
  es: {
    step_basic: "Datos",
    step_kyc: "Identidad",
    step_done: "Listo",
    country_UY: "Uruguay",
    country_AR: "Argentina",
    country_BR: "Brasil",
    country_MX: "México",
    country_CL: "Chile",
    country_CO: "Colombia",
    err_no_token: "Link inválido. Pedile al bot un nuevo link de registro.",
    err_expired: "Link inválido o expirado. Pedile al bot un nuevo link.",
    err_complete: "Error finalizando el registro",
    err_save: "Error guardando datos",
    err_verify: "Error verificando identidad",
    err_photo_size: "Cada foto tiene que pesar menos de 10MB.",
    err_photo_unreadable: "No pudimos leer esa foto. Elegila de nuevo o sacala con la cámara.",
    err_network: "No pudimos enviar las fotos. Revisá tu conexión y volvé a intentar.",
    err_mismatch: "Los datos que escribiste no coinciden con los de tu documento. Revisalos y volve a intentar.",
    err_rejected: "No pudimos verificar tu identidad con esas fotos. Revisá que se vean bien y volvé a intentar.",
    title_invalid: "Link inválido",
    retry_title: "Tu identidad ya está *verificada*",
    retry_sub: "No pudimos finalizar tu registro. Reintentá en un momento.",
    retry_btn: "Reintentar",
    retry_used: "Este link ya no es válido. Si ya completaste tu registro, tu cuenta está lista: volvé a WhatsApp. Si no, pedile un link nuevo al bot.",
    verify_title: "Verificando tu *link*",
    verify_sub: "Danos un segundo.",
    processing: "Procesando...",
    banner_label: "Tenés dinero esperándote",
    banner_value: "[sender] te envió $[amount] USD",
    basic_title: "Contanos *quién sos*",
    basic_sub: "Necesitamos estos datos para cumplir con las regulaciones financieras internacionales.",
    f_name: "Nombre completo",
    ph_name: "Como aparece en tu documento",
    f_dob: "Fecha de nacimiento",
    ph_dob: "DD/MM/AAAA",
    err_dob: "Revisá la fecha de nacimiento: usá día, mes y año (DD/MM/AAAA).",
    f_country: "País de residencia",
    f_phone: "Tu número de WhatsApp",
    continue: "Continuar →",
    back: "← Volver a mis datos",
    kyc_title: "Verificá tu *identidad*",
    kyc_sub: "Subí una foto de cada lado de tu documento y una selfie. Menos de 2 minutos.",
    slot_front: "Frente del documento",
    tip_front: "Las 4 esquinas visibles, sin reflejos",
    slot_back: "Dorso del documento",
    tip_back: "El lado con el código de barras",
    slot_selfie: "Selfie",
    tip_selfie: "Tu cara centrada, con buena luz",
    change: "Cambiar",
    notice: "🔒 Tus documentos son procesados por un proveedor certificado. Zer0 no almacena fotos de documentos ni selfies.",
    kyc_btn: "Verificar identidad →",
    done_money_title: "¡Tu dinero está listo!",
    done_title: "¡Todo *listo!*",
    done_money_sub: "Volvé a WhatsApp para recibir los $[amount] USD que te enviaron.",
    done_sub: "Tu cuenta está verificada. Volvé a WhatsApp para empezar a usar Zer0.",
    back_wa: "Volver a WhatsApp",
    terms: "Términos",
    privacy: "Privacidad",
  },
  pt: {
    step_basic: "Dados",
    step_kyc: "Identidade",
    step_done: "Pronto",
    country_UY: "Uruguai",
    country_AR: "Argentina",
    country_BR: "Brasil",
    country_MX: "México",
    country_CL: "Chile",
    country_CO: "Colômbia",
    err_no_token: "Link inválido. Peça ao bot um novo link de cadastro.",
    err_expired: "Link inválido ou expirado. Peça ao bot um novo link.",
    err_complete: "Erro ao finalizar o cadastro",
    err_save: "Erro ao salvar os dados",
    err_verify: "Erro ao verificar a identidade",
    err_photo_size: "Cada foto precisa ter menos de 10MB.",
    err_photo_unreadable: "Não conseguimos ler essa foto. Escolha de novo ou tire com a câmera.",
    err_network: "Não conseguimos enviar as fotos. Verifique sua conexão e tente de novo.",
    err_mismatch: "Os dados que você digitou não coincidem com os do seu documento. Revise e tente novamente.",
    err_rejected: "Não conseguimos verificar sua identidade com essas fotos. Confira se estão nítidas e tente novamente.",
    title_invalid: "Link inválido",
    retry_title: "Sua identidade já está *verificada*",
    retry_sub: "Não conseguimos finalizar seu cadastro. Tente novamente em instantes.",
    retry_btn: "Tentar novamente",
    retry_used: "Este link não é mais válido. Se você já concluiu o cadastro, sua conta está pronta: volte ao WhatsApp. Se não, peça um novo link ao bot.",
    verify_title: "Verificando seu *link*",
    verify_sub: "Só um instante.",
    processing: "Processando...",
    banner_label: "Você tem dinheiro esperando",
    banner_value: "[sender] enviou $[amount] USD para você",
    basic_title: "Conte *quem você é*",
    basic_sub: "Precisamos desses dados para cumprir as regulamentações financeiras internacionais.",
    f_name: "Nome completo",
    ph_name: "Como aparece no seu documento",
    f_dob: "Data de nascimento",
    ph_dob: "DD/MM/AAAA",
    err_dob: "Confira a data de nascimento: use dia, mês e ano (DD/MM/AAAA).",
    f_country: "País de residência",
    f_phone: "Seu número de WhatsApp",
    continue: "Continuar →",
    back: "← Voltar aos meus dados",
    kyc_title: "Verifique sua *identidade*",
    kyc_sub: "Envie uma foto de cada lado do seu documento e uma selfie. Menos de 2 minutos.",
    slot_front: "Frente do documento",
    tip_front: "Os 4 cantos visíveis, sem reflexos",
    slot_back: "Verso do documento",
    tip_back: "O lado com o código de barras",
    slot_selfie: "Selfie",
    tip_selfie: "Seu rosto centralizado, com boa luz",
    change: "Trocar",
    notice: "🔒 Seus documentos são processados por um provedor certificado. A Zer0 não armazena fotos de documentos nem selfies.",
    kyc_btn: "Verificar identidade →",
    done_money_title: "Seu dinheiro está pronto!",
    done_title: "Tudo *pronto!*",
    done_money_sub: "Volte ao WhatsApp para receber os $[amount] USD que enviaram para você.",
    done_sub: "Sua conta está verificada. Volte ao WhatsApp para começar a usar a Zer0.",
    back_wa: "Voltar ao WhatsApp",
    terms: "Termos",
    privacy: "Privacidade",
  },
  en: {
    step_basic: "Details",
    step_kyc: "Identity",
    step_done: "Done",
    country_UY: "Uruguay",
    country_AR: "Argentina",
    country_BR: "Brazil",
    country_MX: "Mexico",
    country_CL: "Chile",
    country_CO: "Colombia",
    err_no_token: "Invalid link. Ask the bot for a new sign-up link.",
    err_expired: "Invalid or expired link. Ask the bot for a new link.",
    err_complete: "Error completing your registration",
    err_save: "Error saving your details",
    err_verify: "Error verifying your identity",
    err_photo_size: "Each photo must be under 10MB.",
    err_photo_unreadable: "We couldn't read that photo. Choose it again or take it with the camera.",
    err_network: "We couldn't send the photos. Check your connection and try again.",
    err_mismatch: "The details you entered don't match your document. Please review them and try again.",
    err_rejected: "We couldn't verify your identity with those photos. Make sure they are clear and try again.",
    title_invalid: "Invalid link",
    retry_title: "Your identity is already *verified*",
    retry_sub: "We couldn't complete your registration. Please try again in a moment.",
    retry_btn: "Try again",
    retry_used: "This link is no longer valid. If you already finished signing up, your account is ready: go back to WhatsApp. If not, ask the bot for a new link.",
    verify_title: "Checking your *link*",
    verify_sub: "Give us a second.",
    processing: "Processing...",
    banner_label: "You have money waiting for you",
    banner_value: "[sender] sent you $[amount] USD",
    basic_title: "Tell us *who you are*",
    basic_sub: "We need these details to comply with international financial regulations.",
    f_name: "Full name",
    ph_name: "As it appears on your document",
    f_dob: "Date of birth",
    ph_dob: "DD/MM/YYYY",
    err_dob: "Check your date of birth: use day, month and year (DD/MM/YYYY).",
    f_country: "Country of residence",
    f_phone: "Your WhatsApp number",
    continue: "Continue →",
    back: "← Back to my details",
    kyc_title: "Verify your *identity*",
    kyc_sub: "Upload a photo of each side of your document and a selfie. Takes less than 2 minutes.",
    slot_front: "Front of the document",
    tip_front: "All 4 corners visible, no glare",
    slot_back: "Back of the document",
    tip_back: "The side with the barcode",
    slot_selfie: "Selfie",
    tip_selfie: "Your face centered, with good light",
    change: "Change",
    notice: "🔒 Your documents are processed by a certified provider. Zer0 does not store document photos or selfies.",
    kyc_btn: "Verify identity →",
    done_money_title: "Your money is ready!",
    done_title: "All *set!*",
    done_money_sub: "Go back to WhatsApp to receive the $[amount] USD that was sent to you.",
    done_sub: "Your account is verified. Go back to WhatsApp to start using Zer0.",
    back_wa: "Back to WhatsApp",
    terms: "Terms",
    privacy: "Privacy",
  },
};

if (process.env.NODE_ENV !== "production") {
  const base = Object.keys(TEXT.es).sort().join("|");
  ["pt", "en"].forEach(l => {
    if (Object.keys(TEXT[l]).sort().join("|") !== base) {
      console.error(`Onboarding TEXT: las claves de "${l}" no coinciden con "es"`);
    }
  });
}

function fallbackLang() {
  const nav = ((typeof navigator !== "undefined" && navigator.language) || "").toLowerCase();
  if (nav.startsWith("pt")) return "pt";
  if (nav.startsWith("en")) return "en";
  return "es";
}

function tr(lang, key, vars) {
  const table = TEXT[lang] || TEXT.es;
  let out = table[key] !== undefined ? table[key] : TEXT.es[key];
  if (vars) {
    Object.keys(vars).forEach(k => { out = out.split(`[${k}]`).join(vars[k] === undefined || vars[k] === null ? "" : vars[k]); });
  }
  return out;
}

// Convierte "texto *enfasis* resto" en nodos con <em>.
function withEm(str) {
  return str.split("*").map((part, i) => (i % 2 ? <em key={i}>{part}</em> : part));
}

const COUNTRIES = [
  { code: "UY", name: "Uruguay",  flag: "🇺🇾", accountLabel: "Número de cuenta / Alias bancario" },
  { code: "AR", name: "Argentina",flag: "🇦🇷", accountLabel: "CBU / Alias / CVU" },
  { code: "BR", name: "Brasil",   flag: "🇧🇷", accountLabel: "Clave PIX" },
  { code: "MX", name: "México",   flag: "🇲🇽", accountLabel: "CLABE interbancaria" },
  { code: "CL", name: "Chile",    flag: "🇨🇱", accountLabel: "RUT / Cuenta bancaria" },
  { code: "CO", name: "Colombia", flag: "🇨🇴", accountLabel: "Número de cuenta bancaria" },
];

const STEPS = [
  { id: "basic",  key: "step_basic" },
  { id: "kyc",    key: "step_kyc" },
  { id: "done",   key: "step_done" },
];

const CSS = `
  @import url('/fonts/fonts.css');

  :root {
    --bg: #0A0B0A;
    --bg-elevated: #111412;
    --bg-card: #151816;
    --border: #1F2321;
    --border-strong: #2A2E2B;
    --text: #F4F4F0;
    --text-muted: #8A8F89;
    --text-dim: #5A5F5A;
    --accent: #B8FF3D;
    --wa-green: #25D366;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  html, body {
    font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    background: var(--bg);
    color: var(--text);
    font-size: 16px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
  }

  ::selection { background: var(--accent); color: var(--bg); }

  /* ── Nav ── */
  .onb-nav {
    position: sticky;
    top: 0;
    z-index: 100;
    padding: 18px 32px;
    background: rgba(10, 11, 10, 0.8);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .onb-logo {
    font-family: 'Instrument Serif', serif;
    font-size: 24px;
    font-weight: 400;
    letter-spacing: -0.02em;
    color: var(--text);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .onb-logo-dot {
    display: inline-block;
    width: 7px; height: 7px;
    background: var(--accent);
    border-radius: 50%;
    margin-bottom: 5px;
    margin-left: 2px;
  }

  /* ── Step indicator ── */
  .onb-steps {
    display: flex;
    align-items: center;
    gap: 0;
  }

  .onb-step {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .onb-step-num {
    width: 26px; height: 26px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 600;
    font-family: 'Geist Mono', monospace;
    flex-shrink: 0;
    transition: all 0.2s;
  }

  .onb-step-num.done   { background: var(--accent); color: var(--bg); border: 1px solid var(--accent); }
  .onb-step-num.active { background: transparent; color: var(--accent); border: 1px solid var(--accent); }
  .onb-step-num.idle   { background: transparent; color: var(--text-dim); border: 1px solid var(--border-strong); }

  .onb-step-label { font-size: 11px; }
  .onb-step-label.done   { color: var(--text-muted); }
  .onb-step-label.active { color: var(--text); font-weight: 500; }
  .onb-step-label.idle   { color: var(--text-dim); }

  .onb-step-sep { width: 24px; height: 1px; background: var(--border-strong); margin: 0 4px; }

  /* ── Content ── */
  .onb-body {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    position: relative;
  }

  .onb-body::before {
    content: '';
    position: fixed;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);
    width: 700px; height: 400px;
    background: radial-gradient(ellipse at center, rgba(184,255,61,0.05), transparent 65%);
    pointer-events: none;
    z-index: 0;
  }

  .onb-card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 460px;
  }

  /* ── Typography ── */
  .onb-h1 {
    font-family: 'Instrument Serif', serif;
    font-size: clamp(28px, 6vw, 36px);
    font-weight: 400;
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: var(--text);
    margin-bottom: 12px;
  }

  .onb-h1 em { font-style: italic; color: var(--accent); }

  .onb-sub {
    font-size: 15px;
    color: var(--text-muted);
    line-height: 1.6;
    font-weight: 300;
    margin-bottom: 36px;
  }

  /* ── Form fields ── */
  .onb-field { margin-bottom: 20px; }

  .onb-label {
    display: block;
    font-family: 'Geist Mono', monospace;
    font-size: 11px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 500;
    margin-bottom: 10px;
  }

  .onb-input {
    width: 100%;
    background: var(--bg-elevated);
    border: 1px solid var(--border-strong);
    border-radius: 12px;
    padding: 13px 16px;
    color: var(--text);
    font-family: inherit;
    font-size: 15px;
    transition: border-color 0.2s;
    appearance: none;
    outline: none;
  }

  .onb-input:focus { border-color: var(--accent); }
  .onb-input:disabled { opacity: 0.35; cursor: not-allowed; }

  /* ── Primary button ── */
  .onb-btn {
    width: 100%;
    padding: 14px 24px;
    background: var(--accent);
    border: none;
    border-radius: 100px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    color: var(--bg);
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .onb-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 30px rgba(184, 255, 61, 0.3);
  }

  .onb-btn:disabled {
    background: var(--bg-elevated);
    color: var(--text-dim);
    cursor: not-allowed;
  }

  .onb-back {
    display: block;
    width: 100%;
    margin-top: 14px;
    padding: 10px;
    background: none;
    border: none;
    font-family: inherit;
    font-size: 14px;
    color: var(--text-muted);
    cursor: pointer;
  }

  .onb-back:hover { color: var(--text); }

  /* ── WhatsApp button ── */
  .onb-wa-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: var(--wa-green);
    color: #06210f;
    padding: 14px 28px;
    border-radius: 100px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s;
    width: 100%;
  }

  .onb-wa-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 30px rgba(37, 211, 102, 0.35);
  }

  /* ── Upload slots ── */
  .onb-upload-grid {
    display: flex;
    gap: 10px;
    margin-bottom: 24px;
  }

  .onb-upload-slot {
    flex: 1;
    min-width: 0;
    aspect-ratio: 3 / 4;
    background: var(--bg-elevated);
    border: 1.5px dashed var(--border-strong);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 6px;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.2s;
    position: relative;
    overflow: hidden;
  }

  .onb-upload-slot:hover { border-color: var(--text-dim); }

  .onb-upload-slot.filled {
    border-style: solid;
    border-color: var(--accent);
    padding: 0;
  }

  .onb-upload-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text);
    line-height: 1.3;
  }

  .onb-upload-tip {
    font-size: 10.5px;
    color: var(--text-dim);
    line-height: 1.3;
  }

  .onb-upload-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .onb-upload-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 6px 4px;
    background: rgba(10, 11, 10, 0.75);
    backdrop-filter: blur(4px);
    color: var(--text);
    font-size: 11px;
    font-weight: 500;
  }

  /* ── Pending banner ── */
  .onb-banner {
    background: rgba(184, 255, 61, 0.06);
    border: 1px solid rgba(184, 255, 61, 0.22);
    border-radius: 14px;
    padding: 16px 20px;
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .onb-banner-icon { font-size: 28px; flex-shrink: 0; line-height: 1; }

  .onb-banner-label { font-size: 12px; color: var(--text-muted); margin-bottom: 3px; }

  .onb-banner-value { font-size: 17px; color: var(--accent); font-weight: 500; }

  /* ── Notice ── */
  .onb-notice {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 24px;
    font-size: 13px;
    color: var(--text-muted);
    line-height: 1.55;
  }

  /* ── Error ── */
  .onb-error {
    background: rgba(248, 113, 113, 0.06);
    border: 1px solid rgba(248, 113, 113, 0.22);
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #F87171;
    line-height: 1.5;
  }

  /* ── Center states ── */
  .onb-center {
    text-align: center;
    padding: 16px 0;
  }

  .onb-state-icon { font-size: 56px; margin-bottom: 28px; line-height: 1; }

  /* ── Footer ── */
  .onb-footer {
    border-top: 1px solid var(--border);
    padding: 20px 32px;
    font-family: 'Geist Mono', monospace;
    font-size: 11px;
    color: var(--text-dim);
    text-align: center;
    letter-spacing: 0.04em;
  }

  .onb-footer a { color: var(--text-dim); text-decoration: none; }
  .onb-footer a:hover { color: var(--accent); }

  /* ── Divider ── */
  .onb-divider { height: 1px; background: var(--border); margin: 24px 0; }

  /* ── Responsive ── */
  @media (max-width: 520px) {
    .onb-nav { padding: 16px 20px; }
    .onb-step-label { display: none; }
    .onb-step-sep { width: 14px; }
    .onb-body { padding: 40px 16px; }
    .onb-footer { padding: 16px 20px; }
  }
`;

async function requestComplete(token, lang) {
  const res = await fetch(`${API}/complete?token=${encodeURIComponent(token)}`, { method: "POST" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || tr(lang, "err_complete"));
    err.status = res.status;
    throw err;
  }
}

function StepIndicator({ currentStep, lang }) {
  const currentIndex = STEPS.findIndex(s => s.id === currentStep);
  return (
    <div className="onb-steps">
      {STEPS.map((s, i) => {
        const done   = currentIndex > i;
        const active = s.id === currentStep;
        const cls    = done ? "done" : active ? "active" : "idle";
        return (
          <div key={s.id} style={{ display: "flex", alignItems: "center" }}>
            <div className="onb-step">
              <div className={`onb-step-num ${cls}`}>{done ? "✓" : i + 1}</div>
              <div className={`onb-step-label ${cls}`}>{tr(lang, s.key)}</div>
            </div>
            {i < STEPS.length - 1 && <div className="onb-step-sep" />}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="onb-field">
      <label className="onb-label">{label}</label>
      {children}
    </div>
  );
}

function ErrorMsg({ children }) {
  return <div className="onb-error">{children}</div>;
}

function Btn({ onClick, loading, disabled, children, lang }) {
  return (
    <button className="onb-btn" onClick={onClick} disabled={loading || disabled}>
      {loading ? tr(lang, "processing") : children}
    </button>
  );
}

function DocFrontExample() {
  return (
    <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
      <rect x="6" y="16" width="52" height="32" rx="5" stroke="var(--border-strong)" strokeWidth="1.5" />
      <rect x="11" y="21" width="14" height="18" rx="2" fill="var(--bg-elevated)" stroke="var(--border-strong)" strokeWidth="1.2" />
      <line x1="30" y1="24" x2="52" y2="24" stroke="var(--text-dim)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="30" y1="30" x2="48" y2="30" stroke="var(--text-dim)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="30" y1="36" x2="50" y2="36" stroke="var(--text-dim)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function DocBackExample() {
  const bars = Array.from({ length: 11 });
  return (
    <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
      <rect x="6" y="16" width="52" height="32" rx="5" stroke="var(--border-strong)" strokeWidth="1.5" />
      <line x1="12" y1="23" x2="52" y2="23" stroke="var(--text-dim)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="29" x2="42" y2="29" stroke="var(--text-dim)" strokeWidth="1.5" strokeLinecap="round" />
      {bars.map((_, i) => (
        <rect key={i} x={12 + i * 3.7} y="35" width={i % 3 === 0 ? 2 : 1.2} height="8" fill="var(--text-dim)" />
      ))}
    </svg>
  );
}

function SelfieExample() {
  return (
    <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
      <rect x="10" y="8" width="44" height="48" rx="14" stroke="var(--border-strong)" strokeWidth="1.5" />
      <ellipse cx="32" cy="32" rx="12" ry="15" stroke="var(--accent)" strokeWidth="1.6" />
    </svg>
  );
}

function UploadSlot({ label, tip, file, onChange, Example, lang }) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      onChange(null, tr(lang, "err_photo_size"));
      return;
    }
    // Copia en memoria: ver fileMemory.js (archivos de la galeria de Android).
    try {
      onChange(await readFileToMemory(f), "");
    } catch {
      onChange(null, tr(lang, "err_photo_unreadable"));
    }
  };

  return (
    <div className={`onb-upload-slot ${file ? "filled" : ""}`} onClick={() => inputRef.current?.click()}>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />
      {file ? (
        <>
          <img src={previewUrl} alt={label} className="onb-upload-preview"
            onError={() => onChange(null, tr(lang, "err_photo_unreadable"))} />
          <div className="onb-upload-overlay">{tr(lang, "change")}</div>
        </>
      ) : (
        <>
          <Example />
          <div className="onb-upload-label">{label}</div>
          <div className="onb-upload-tip">{tip}</div>
        </>
      )}
    </div>
  );
}

export default function Onboarding() {
  const [step, setStep]       = useState("verify");
  const [token, setToken]     = useState("");
  const [context, setContext] = useState({});
  const [phone, setPhone]     = useState("");
  const [error, setError]     = useState("");
  const [errorStatus, setErrorStatus] = useState(0);
  const [lang, setLang]       = useState(fallbackLang);
  const [loading, setLoading] = useState(false);

  const [fullName,     setFullName]     = useState("");
  const [dobText,      setDobText]      = useState("");
  const dob = dobTextToIso(dobText);
  const [country,      setCountry]      = useState("UY");

  const [docFront, setDocFront] = useState(null);
  const [docBack,  setDocBack]  = useState(null);
  const [selfie,   setSelfie]   = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) { setError(tr(fallbackLang(), "err_no_token")); setStep("error"); return; }
    setToken(t);
    verifyToken(t);
  }, []);

  const verifyToken = async (t) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/verify-token?token=${encodeURIComponent(t)}`);
      if (!res.ok) throw new Error("Token inválido");
      const data = await res.json();
      setPhone(data.phone);
      const userLang = Object.prototype.hasOwnProperty.call(TEXT, data.lang) ? data.lang : fallbackLang();
      setLang(userLang);
      setContext(data.context || {});
      // Paso 1 siempre con lo que el usuario ya contesto (en WhatsApp o antes),
      // para que revise o complete lo que falta.
      const prefill = data.basic_data || {};
      if (prefill.full_name) setFullName(prefill.full_name);
      if (prefill.dob) setDobText(isoToDobText(prefill.dob));
      if (COUNTRIES.some(c => c.code === prefill.country_code)) setCountry(prefill.country_code);
      if (data.kyc_status === "verified" && data.has_basic_data) {
        try {
          await requestComplete(t, userLang);
          setStep("done");
        } catch (e) {
          setError(e.message);
          setErrorStatus(e.status || 0);
          setStep("retry");
        }
      } else setStep("basic");
    } catch {
      setError(tr(fallbackLang(), "err_expired"));
      setStep("error");
    }
    setLoading(false);
  };

  const handleBasicData = async () => {
    if (!fullName.trim() || !dob || !country) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/basic-data`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, full_name: fullName, dob, country_code: country })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || tr(lang, "err_save"));
      setStep("kyc");
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleKycSubmit = async () => {
    if (!docFront || !docBack || !selfie) return;
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("front", docFront);
      fd.append("back", docBack);
      fd.append("selfie", selfie);
      const res = await fetch(`${API}/kyc-submit`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || tr(lang, "err_verify"));
      if (data.status === "rejected" && data.reason === "data_mismatch") {
        setError(tr(lang, "err_mismatch"));
        setDocFront(null); setDocBack(null); setSelfie(null);
        setFullName(""); setDobText("");
        setStep("basic");
      } else if (data.status === "rejected") {
        setError(tr(lang, "err_rejected"));
        setDocFront(null); setDocBack(null); setSelfie(null);
      } else {
        try {
          await requestComplete(token, lang);
          setStep("done");
        } catch (e) {
          setError(e.message);
          setErrorStatus(e.status || 0);
          setStep("retry");
        }
      }
    } catch (e) {
      // fetch lanza TypeError ("Failed to fetch") cuando la peticion no llega a
      // completarse; los errores del servidor ya traen su mensaje.
      setError(e instanceof TypeError ? tr(lang, "err_network") : e.message);
    }
    setLoading(false);
  };

  const handleRetryComplete = async () => {
    setLoading(true); setError("");
    try {
      await requestComplete(token, lang);
      setStep("done");
    } catch (e) { setError(e.message); setErrorStatus(e.status || 0); }
    setLoading(false);
  };

  const pendingAmount    = context.amount;
  const pendingSender    = context.sender_name;
  const showSteps        = !["error", "verify", "retry", "done"].includes(step);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{CSS}</style>

      {/* Nav */}
      <nav className="onb-nav">
        <span className="onb-logo">
          Zer0<span className="onb-logo-dot" />
        </span>
        {showSteps && <StepIndicator currentStep={step} lang={lang} />}
      </nav>

      {/* Content */}
      <div className="onb-body" style={{ flex: 1 }}>
        <div className="onb-card">

          {/* Error */}
          {step === "error" && (
            <div className="onb-center">
              <div className="onb-state-icon">⚠️</div>
              <div className="onb-h1">{tr(lang, "title_invalid")}</div>
              <p className="onb-sub">{error}</p>
            </div>
          )}

          {/* Identidad verificada, falta finalizar el registro */}
          {step === "retry" && (
            <div className="onb-center">
              <div className="onb-state-icon">⚠️</div>
              <div className="onb-h1">{withEm(tr(lang, "retry_title"))}</div>
              {errorStatus === 401 ? (
                <p className="onb-sub">{tr(lang, "retry_used")}</p>
              ) : (
                <>
                  <p className="onb-sub">{tr(lang, "retry_sub")}</p>
                  {error && <ErrorMsg>{error}</ErrorMsg>}
                  <Btn onClick={handleRetryComplete} loading={loading} lang={lang}>{tr(lang, "retry_btn")}</Btn>
                </>
              )}
            </div>
          )}

          {/* Verificando */}
          {step === "verify" && (
            <div className="onb-center">
              <div className="onb-state-icon">🔍</div>
              <div className="onb-h1">{withEm(tr(lang, "verify_title"))}</div>
              <p className="onb-sub">{tr(lang, "verify_sub")}</p>
            </div>
          )}

          {/* Paso 1 — Datos básicos */}
          {step === "basic" && (
            <div>
              {pendingAmount && (
                <div className="onb-banner">
                  <div className="onb-banner-icon">💰</div>
                  <div>
                    <div className="onb-banner-label">{tr(lang, "banner_label")}</div>
                    <div className="onb-banner-value">{tr(lang, "banner_value", { sender: pendingSender, amount: pendingAmount })}</div>
                  </div>
                </div>
              )}
              <div className="onb-h1">{withEm(tr(lang, "basic_title"))}</div>
              <p className="onb-sub">{tr(lang, "basic_sub")}</p>

              <Field label={tr(lang, "f_name")}>
                <input className="onb-input" value={fullName} onChange={e => setFullName(e.target.value)}
                  placeholder={tr(lang, "ph_name")} />
              </Field>
              <Field label={tr(lang, "f_dob")}>
                <input className="onb-input" type="text" inputMode="numeric" autoComplete="bday"
                  maxLength={10} value={dobText} onChange={e => setDobText(maskDobInput(e.target.value))}
                  placeholder={tr(lang, "ph_dob")} />
                {dobText.length === 10 && !dob && <ErrorMsg>{tr(lang, "err_dob")}</ErrorMsg>}
              </Field>
              <Field label={tr(lang, "f_country")}>
                <select className="onb-input" value={country} onChange={e => setCountry(e.target.value)}>
                  {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {tr(lang, `country_${c.code}`)}</option>)}
                </select>
              </Field>
              <Field label={tr(lang, "f_phone")}>
                <input className="onb-input" value={phone} disabled />
              </Field>

              {error && <ErrorMsg>{error}</ErrorMsg>}
              <Btn onClick={handleBasicData} loading={loading} disabled={!fullName || !dob} lang={lang}>
                {tr(lang, "continue")}
              </Btn>
            </div>
          )}

          {/* Paso 2 — KYC */}
          {step === "kyc" && (
            <div>
              <div className="onb-h1">{withEm(tr(lang, "kyc_title"))}</div>
              <p className="onb-sub">{tr(lang, "kyc_sub")}</p>

              <div className="onb-upload-grid">
                <UploadSlot
                  label={tr(lang, "slot_front")}
                  tip={tr(lang, "tip_front")}
                  lang={lang}
                  file={docFront}
                  Example={DocFrontExample}
                  onChange={(f, err) => { setDocFront(f); setError(err); }}
                />
                <UploadSlot
                  label={tr(lang, "slot_back")}
                  tip={tr(lang, "tip_back")}
                  lang={lang}
                  file={docBack}
                  Example={DocBackExample}
                  onChange={(f, err) => { setDocBack(f); setError(err); }}
                />
                <UploadSlot
                  label={tr(lang, "slot_selfie")}
                  tip={tr(lang, "tip_selfie")}
                  lang={lang}
                  file={selfie}
                  Example={SelfieExample}
                  onChange={(f, err) => { setSelfie(f); setError(err); }}
                />
              </div>

              <div className="onb-notice">
                {tr(lang, "notice")}
              </div>

              {error && <ErrorMsg>{error}</ErrorMsg>}
              <Btn onClick={handleKycSubmit} loading={loading} disabled={!docFront || !docBack || !selfie} lang={lang}>
                {tr(lang, "kyc_btn")}
              </Btn>
              <button type="button" className="onb-back" onClick={() => { setError(""); setStep("basic"); }} disabled={loading}>
                {tr(lang, "back")}
              </button>
            </div>
          )}

          {/* Paso 3: Listo */}
          {step === "done" && (
            <div className="onb-center">
              <div className="onb-state-icon">✅</div>
              <div className="onb-h1">
                {pendingAmount ? tr(lang, "done_money_title") : <span>{withEm(tr(lang, "done_title"))}</span>}
              </div>
              <p className="onb-sub" style={{ marginBottom: 32 }}>
                {pendingAmount
                  ? tr(lang, "done_money_sub", { amount: pendingAmount })
                  : tr(lang, "done_sub")
                }
              </p>
              <a className="onb-wa-btn" href="https://wa.me/59893807348">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.79.47 3.55 1.36 5.09L2 22l5.25-1.38c1.47.81 3.13 1.24 4.79 1.24 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm5.79 14.18c-.24.68-1.41 1.32-1.95 1.4-.5.07-1.13.1-1.83-.12-.42-.13-.96-.31-1.65-.61-2.91-1.26-4.81-4.18-4.95-4.37-.14-.19-1.18-1.57-1.18-3 0-1.42.75-2.12 1.01-2.41.27-.29.58-.36.78-.36.19 0 .39 0 .56.01.18.01.42-.07.66.5.24.58.83 2 .9 2.15.07.15.12.32.02.51-.1.19-.15.31-.29.48-.14.17-.3.37-.42.5-.14.15-.29.3-.12.59.16.29.72 1.19 1.55 1.93 1.07.95 1.96 1.25 2.25 1.39.29.14.46.12.64-.07.18-.19.75-.87.95-1.17.2-.29.4-.24.66-.14.27.1 1.74.82 2.04.97.3.15.5.22.57.35.07.13.07.74-.18 1.46z"/>
                </svg>
                {tr(lang, "back_wa")}
              </a>
            </div>
          )}

        </div>
      </div>

      {/* Footer */}
      <footer className="onb-footer">
        © 2026 Zer0 App Ltd · <a href="https://www.zer0.lat/terms" target="_blank" rel="noopener noreferrer">{tr(lang, "terms")}</a> · <a href="https://www.zer0.lat/privacy" target="_blank" rel="noopener noreferrer">{tr(lang, "privacy")}</a>
      </footer>
    </div>
  );
}
